import {
	type AnyArityOneFunction,
	type ArityOneParameter,
} from '@post-office/shared-contracts/pipeline/contraints';

import {
	type AnyBackendMessageStage,
	type AnyBackendPlacementStage,
	type AnyPayload,
	type AnySyncBackendMessageStage,
	type PlacementBackendRuntimeResult,
	type RequestContext,
	type SyncRequestContext,
} from '../../types';

const HISTOGRAM_LATENCY_INTERVAL_BUCKETS = [25, 50, 100, 150, 200, 250, 500, 1000];

const createTimedStage =
	<T extends (params: { request: SyncRequestContext }) => unknown>(
		timerName: string,
		tag?: ({
			input,
			output,
		}: {
			input: ArityOneParameter<T>;
			output?: Awaited<ReturnType<T>>;
		}) => AnyPayload,
	) =>
	(stage: T): T =>
		(async (params: ArityOneParameter<T>) => {
			params.request.logger.info('Started stage');

			const startTime = performance.now();

			const result = stage(params);

			if (!(result instanceof Promise)) {
				const unwrappedResult = result as Awaited<ReturnType<T>>;

				let tags: AnyPayload | undefined;
				try {
					tags = tag?.({ input: params, output: unwrappedResult });
				} catch {
					params.request.logger.warn('Unable to apply tags to stage logs');
				}

				const latencyInMs = performance.now() - startTime;

				// eslint-disable-next-line @atlassian/incredible/safe-logging-practices
				params.request.logger.info({ latencyInMs, ...tags }, 'Finished stage');

				return result as ReturnType<T>;
			}

			const { stop } = params.request.metrics
				.histogram(timerName, HISTOGRAM_LATENCY_INTERVAL_BUCKETS)
				.measure();

			try {
				const unwrappedResult = (await result) as Awaited<ReturnType<T>>;

				let tags: AnyPayload | undefined;
				try {
					tags = tag?.({ input: params, output: unwrappedResult });
				} catch {
					params.request.logger.warn('Unable to apply tags to stage logs');
				}

				const duration = performance.now() - startTime;
				// eslint-disable-next-line @atlassian/incredible/safe-logging-practices
				params.request.logger.info(
					{
						duration,
						timerName,
						...tags,
					},
					'Finished stage',
				);

				return unwrappedResult;
			} finally {
				stop();
			}
		}) as T;

const createTimedSystemStage =
	<T extends AnyArityOneFunction>(fn: (context: { request: SyncRequestContext }) => T) =>
	(contexts: { request: RequestContext }): T => {
		return (async (params: ArityOneParameter<T>) => {
			contexts.request.logger.info('Started stage');
			const startTime = performance.now();

			const result = fn(contexts)(params);

			if (!(result instanceof Promise)) {
				const duration = performance.now() - startTime;
				// eslint-disable-next-line @atlassian/incredible/safe-logging-practices
				contexts.request.logger.info({ duration }, 'Finished stage');

				return result as ReturnType<T>;
			}

			const timerName = 'post-office.placement-v2.runtime.execute';

			const { stop } = contexts.request.metrics
				.histogram(timerName, HISTOGRAM_LATENCY_INTERVAL_BUCKETS)
				.measure();

			try {
				return (await result) as ReturnType<T>;
			} finally {
				stop();

				const latencyInMs = performance.now() - startTime;
				// eslint-disable-next-line @atlassian/incredible/safe-logging-practices
				contexts.request.logger.info({ latencyInMs, timerName }, 'Finished stage');
			}
		}) as T;
	};

export const messageTimedStage = createTimedStage<
	AnyBackendMessageStage | AnySyncBackendMessageStage
>('post-office.placement-v2.stage-execute');

export const placementTimedStage = createTimedStage<AnyBackendPlacementStage>(
	'post-office.placement-v2.stage-execute',
	({ input, output }) => {
		if (!output) {
			return {};
		}

		const inputMessageTemplateIds = Array.from(
			new Set(input.data.messages.map((message) => message.messageTemplateId)).values(),
		);

		const outputMessageTemplateIds = Array.from(
			new Set(output.messages.map((message) => message.messageTemplateId)).values(),
		);

		const deltaMessageTemplateIds = inputMessageTemplateIds.filter(
			(id) => !outputMessageTemplateIds.includes(id),
		);

		const inputMessageInstanceIds = input.data.messages.map(
			({ messageInstanceId }) => messageInstanceId,
		);

		const outputMessageInstanceIds = output.messages.map(
			({ messageInstanceId }) => messageInstanceId,
		);

		const deltaMessageInstanceIds = inputMessageInstanceIds.filter(
			(id) => !outputMessageInstanceIds.includes(id),
		);

		return {
			input: {
				number: input.data.messages.length,
				messageTemplateIds: inputMessageTemplateIds,
				messageInstanceIds: inputMessageInstanceIds,
			},
			output: {
				number: output.messages.length,
				messageTemplateIds: outputMessageTemplateIds,
				messageInstanceIds: outputMessageInstanceIds,
			},
			delta: {
				number: output.messages.length - input.data.messages.length,
				messageTemplateIds: deltaMessageTemplateIds,
				messageInstanceIds: deltaMessageInstanceIds,
			},
		};
	},
);

export const placementRuntimeTimedStage = createTimedSystemStage;

export const logRuntimeResult =
	({ request }: { request: RequestContext }) =>
	(result: PlacementBackendRuntimeResult): void => {
		const errorMessageTemplateIds = Array.from(
			new Set(result.errors.map((e) => e.messageTemplateId)).values(),
		);

		const messageTemplateIds = Array.from(
			new Set(result.data.messages.map((e) => e.messageTemplateId)).values(),
		);

		const formattedMessages = result.data.messages.map(
			({
				messageTemplateId,
				messageInstanceId,
				messageCreationType,
				messageCategory,
				triggerId,
			}) => ({
				messageTemplateId,
				messageInstanceId,
				messageCreationType,
				messageCategory,
				triggerId,
			}),
		);

		const formattedErrorDetails = result.errors.map(
			({ messageTemplateId, messageInstanceId, reason }) => ({
				messageTemplateId,
				messageInstanceId,
				reason,
			}),
		);

		request.logger.info(
			{
				errors: {
					number: result.errors.length,
					messageTemplateIds: errorMessageTemplateIds,
					messageDetails: formattedErrorDetails,
				},
				messages: {
					number: result.data.messages.length,
					messageTemplateIds: messageTemplateIds,
					messageDetails: formattedMessages,
				},
			},
			'Runtime result',
		);
	};
